/**
 * Pure decisions for the foliar sign-off queue (F3).
 *
 * No React, no network, no i18n. Mirrors soilSignOff.ts's row-shaping half —
 * but the ageing rules are NOT restated here. They live in ./signOffAgeing,
 * which soil re-exports, so the two queues and the two detail screens share
 * ONE definition of how old a row is and what colour that earns (spec §4.4).
 *
 * The row id is the sessionId. Soil needs a sessionId + groupId pair because a
 * soil session carries several groups; a foliar session IS one orchard.
 */
import type { FoliarAwaitingRow } from '../../services/nutritionService';
import {
  WaitingTone, WAITING_WARN_DAYS, waitingDaysSince, waitingTone,
} from './signOffAgeing';

export interface FoliarSignOffRow extends FoliarAwaitingRow {
  waitingDays: number;
  tone: WaitingTone;
  label: string;
}

export function buildSignOffRows(items: FoliarAwaitingRow[], now: Date): FoliarSignOffRow[] {
  return (items || []).map((it) => {
    const days = waitingDaysSince(it.completedAt, now);
    return {
      ...it,
      waitingDays: days,
      tone: waitingTone(days),
      // Used by the partial-failure notice, so it must identify the row to a
      // human without the table in front of them.
      label: `${it.roundName} · ${it.orchardNo}`,
    };
  });
}

export function filterSignOffRows(
  rows: FoliarSignOffRow[], search: string, overThresholdOnly: boolean,
): FoliarSignOffRow[] {
  const q = (search || '').trim().toLowerCase();
  return (rows || []).filter((r) => {
    // AND, not OR: a manager who has both narrowed the search and asked for
    // "over N days" means both.
    if (overThresholdOnly && r.waitingDays < WAITING_WARN_DAYS) return false;
    if (!q) return true;
    const hay = [r.orchardNo, r.roundName, r.season || '']
      .concat(r.operatorNames || []).join(' ').toLowerCase();
    return hay.indexOf(q) !== -1;
  });
}

export function toBulkItems(
  rows: FoliarSignOffRow[], selectedIds: string[],
): Array<{ sessionId: string }> {
  const byId: Record<string, FoliarSignOffRow> = {};
  (rows || []).forEach((r) => { byId[r.sessionId] = r; });
  // A selection deliberately survives a filter change, so an id can outlive
  // its row across a refresh. Drop it rather than sending { sessionId:
  // undefined }, which the route would 400 on and blame the whole batch for.
  return (selectedIds || []).map((id) => byId[id]).filter(Boolean).map((r) => ({ sessionId: r.sessionId }));
}

/** Labels for the items a bulk run did not sign off, so a partial result is named. */
export function failedLabels(
  rows: FoliarSignOffRow[], results: Array<{ sessionId: string; ok: boolean }>,
): string[] {
  return (results || [])
    .filter((r) => !r.ok)
    .map((r) => (rows || []).find((row) => row.sessionId === r.sessionId))
    .filter(Boolean)
    .map((row) => (row as FoliarSignOffRow).label);
}
